import React, { FunctionComponent, ReactNode } from 'react'
import clsx from 'clsx'
import { Grid, useDesignLanguagePerClass, Text, Skeleton, Slide, ColorTypes, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../../utils/responsive'
import { Money, MoneyPrefix } from '../../Money'
import { useTranslation } from '../../../utils/convergence'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import styles from './styles.module.css'
import { Money as MoneyModel, SupportedLocale } from '@afterpay/types'

interface SlideWrapperProps {
  children: ReactNode
  shouldSlideIn?: boolean
  testNameSpace?: string
}

interface OrderDetailsBreakdownProps {
  label: string
  value?: MoneyModel
  loading?: boolean
  boldLabel?: boolean
  boldMoney?: boolean
  locale?: SupportedLocale
  slideMoneyIn?: boolean
  isDiscount?: boolean
  testNameSpace?: string
}

export const OrderDetailsBreakdown: FunctionComponent<OrderDetailsBreakdownProps> = ({
  label,
  value,
  loading = false,
  boldLabel = false,
  boldMoney = false,
  locale,
  slideMoneyIn = false,
  isDiscount = false,
  testNameSpace = '',
}) => {
  const { t } = useTranslation()
  const { textSize } = useCheckoutSizes()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const themeClass = useDesignLanguagePerClass(styles)
  const shouldApplyTestNameSpace = testNameSpace.length > 0
  const appliedTestNameSpace = shouldApplyTestNameSpace ? `order-details-breakdown-${testNameSpace}` : undefined

  const moneyColor: ColorTypes = isDiscount && isArcadeDesignLanguage ? 'Success' : 'Black'
  const moneyPrefix = isDiscount ? MoneyPrefix.NEGATIVE : MoneyPrefix.NONE

  return (
    <div className={clsx(themeClass.amountBreakdown, styles.container)}>
      <Grid>
        <Grid.Item>
          <Text className={styles.title} bold={boldLabel} size={textSize} {...(shouldApplyTestNameSpace && { testNameSpace: `${appliedTestNameSpace}-label` })}>
            {label}
          </Text>
        </Grid.Item>
        <Grid.Item>
          {valueIsNotNullOrUndefined(value) ? (
            <SlideWrapper shouldSlideIn={slideMoneyIn} testNameSpace={appliedTestNameSpace}>
              <Money
                color={moneyColor}
                locale={locale}
                size={textSize}
                bold={boldMoney}
                value={value}
                loading={loading}
                prefix={moneyPrefix}
                testNameSpace={appliedTestNameSpace}
              />
            </SlideWrapper>
          ) : (
            <Text className={styles.title} renderAs='div' size={textSize} testNameSpace={appliedTestNameSpace}>
              <span className={loading ? styles.visuallyInvisible : ''}>{t('summary:includedInTotal')}</span>
              {loading && <Skeleton testNameSpace={appliedTestNameSpace} />}
            </Text>
          )}
        </Grid.Item>
      </Grid>
    </div>
  )
}

const SlideWrapper: FunctionComponent<SlideWrapperProps> = ({ children, shouldSlideIn, testNameSpace = '' }) => {
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)

  if (isBronteDesignLanguage || !shouldSlideIn) {
    return <>{children}</>
  }

  return (
    <Slide.In from='Left' translateBy='Half' fade testNameSpace={testNameSpace}>
      {children}
    </Slide.In>
  )
}
